/**
 * ✅ tRPC标准错误码（与后端完全一致）
 * 
 * 注意：为避免在共享包中依赖 @trpc/server，直接定义类型
 * 这与 @trpc/server/rpc 的 TRPC_ERROR_CODE_KEY 完全一致
 */
export type ErrorCode =
  | 'PARSE_ERROR'
  | 'BAD_REQUEST'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'METHOD_NOT_SUPPORTED'
  | 'TIMEOUT'
  | 'CONFLICT'
  | 'PRECONDITION_FAILED'
  | 'PAYLOAD_TOO_LARGE'
  | 'UNPROCESSABLE_CONTENT'
  | 'TOO_MANY_REQUESTS'
  | 'CLIENT_CLOSED_REQUEST'
  | 'INTERNAL_SERVER_ERROR';

/**
 * tRPC 客户端错误结构（用于类型检查）
 */
interface TRPCClientErrorLike {
  message?: string;
  data?: {
    code?: string | number;
    httpStatus?: number;
    trpcCode?: string;
    message?: string; // ✅ 某些情况下消息可能在 data 中
    zodError?: {
      issues?: Array<{
        path: (string | number)[];
        message: string;
        code: string;
      }>;
    };
    details?: Record<string, unknown>;
    [key: string]: unknown;
  };
  shape?: {
    code?: string;
    message?: string; // ✅ 某些 tRPC 版本消息在 shape 中
    data?: {
      code?: string | number;
      httpStatus?: number;
      trpcCode?: string;
      message?: string; // ✅ 某些情况下消息可能在 shape.data 中
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };
  meta?: {
    response?: {
      status?: number;
      statusText?: string;
    };
    responseJSON?: Array<{
      error?: {
        message?: string;
        code?: string;
        data?: {
          code?: string | number;
          httpStatus?: number;
          trpcCode?: string;
          message?: string; // ✅ 某些情况下消息可能在 responseJSON 中
          zodError?: Record<string, unknown>;
          details?: Record<string, unknown>;
        };
      };
    }>;
  };
  cause?: unknown; // ✅ tRPC 客户端错误的 cause 可能包含原始错误
  [key: string]: unknown;
}


/**
 * ✅ 简化的错误信息接口
 */
export interface ErrorInfo {
  /** tRPC标准错误码 */
  code: ErrorCode;
  /** 错误消息 */
  message: string;
  /** HTTP状态码 */
  httpStatus?: number;
  /** Zod验证错误详情 */
  zodError?: {
    issues: Array<{
      path: (string | number)[];
      message: string;
      code: string;
    }>;
  };
  /** 额外的错误详情 */
  details?: Record<string, unknown>;
  /** 原始错误对象（用于调试） */
  originalError?: unknown;
}

/**
 * ✅ tRPC标准错误码列表
 */
const VALID_TRPC_CODES: ErrorCode[] = [
  'PARSE_ERROR',
  'BAD_REQUEST',
  'UNAUTHORIZED',
  'FORBIDDEN',
  'NOT_FOUND',
  'METHOD_NOT_SUPPORTED',
  'TIMEOUT',
  'CONFLICT',
  'PRECONDITION_FAILED',
  'PAYLOAD_TOO_LARGE',
  'UNPROCESSABLE_CONTENT',
  'TOO_MANY_REQUESTS',
  'CLIENT_CLOSED_REQUEST',
  'INTERNAL_SERVER_ERROR',
];

/**
 * ✅ 验证是否为有效的tRPC错误码
 */
function isValidTRPCCode(code: string): code is ErrorCode {
  return VALID_TRPC_CODES.includes(code as ErrorCode);
}

/**
 * ✅ HTTP状态码映射到tRPC错误码（与后端一致）
 */
function mapHttpStatusToTRPCCode(status: number): ErrorCode {
  const codeMap: Record<number, ErrorCode> = {
    400: 'BAD_REQUEST',
    401: 'UNAUTHORIZED',
    403: 'FORBIDDEN',
    404: 'NOT_FOUND',
    405: 'METHOD_NOT_SUPPORTED',
    408: 'TIMEOUT',
    409: 'CONFLICT',
    412: 'PRECONDITION_FAILED',
    413: 'PAYLOAD_TOO_LARGE',
    422: 'UNPROCESSABLE_CONTENT',
    429: 'TOO_MANY_REQUESTS',
    499: 'CLIENT_CLOSED_REQUEST',
    500: 'INTERNAL_SERVER_ERROR',
  };
  
  return codeMap[status] || (status >= 500 ? 'INTERNAL_SERVER_ERROR' : 'BAD_REQUEST');
}

/**
 * ✅ 检测网络是否离线（宽带/WiFi中断）
 * 
 * 使用 navigator.onLine API 检测网络连接状态
 */
function isNetworkOffline(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }
  
  // navigator.onLine 可能不总是准确，但可以作为参考
  return !navigator.onLine;
}

/**
 * ✅ 检测是否为网络错误（基于错误结构特征，符合最佳实践）
 * 
 * 网络错误的特征：
 * 1. 没有 HTTP 响应（meta.response 为 undefined）
 * 2. 没有 HTTP 状态码（data.httpStatus 为 undefined）
 * 3. 错误类型通常是 TypeError（fetch 失败时）
 * 
 * 字符串匹配仅作为兜底方案
 */
function checkNetworkError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  
  const errorObj = error as TRPCClientErrorLike;
  
  // ✅ 优先检查：是否有 HTTP 响应
  // 网络错误通常没有收到 HTTP 响应
  const hasHttpResponse = 
    errorObj.data?.httpStatus !== undefined ||
    errorObj.meta?.response?.status !== undefined;
  
  if (hasHttpResponse) {
    // 有 HTTP 响应，不是网络错误
    return false;
  }
  
  // ✅ 检查错误类型：fetch 失败通常是 TypeError
  if (error instanceof TypeError) {
    return true;
  }
  
  // ✅ 检查错误名称
  const errorName = (error as { name?: string }).name;
  if (errorName === 'TypeError' || errorName === 'NetworkError') {
    return true;
  }
  
  // ✅ 兜底：检查错误消息（仅当无法通过结构特征判断时）
  const message = errorObj.message;
  if (typeof message === 'string') {
    // 只检查最明确的网络错误消息
    const networkErrorPatterns = [
      'Failed to fetch',
      'fetch failed',
      'NetworkError',
      'ERR_NETWORK',
      'ECONNREFUSED',
      'ENOTFOUND',
    ];
    
    return networkErrorPatterns.some(pattern => 
      message.includes(pattern)
    );
  }
  
  return false;
}

/**
 * ✅ 检测是否为超时错误（内部检测函数）
 */
function checkTimeoutError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  
  const errorObj = error as TRPCClientErrorLike;
  
  // 检查 HTTP 状态码
  const httpStatus = 
    errorObj.data?.httpStatus || 
    errorObj.meta?.response?.status;
  
  if (httpStatus === 408) {
    return true;
  }
  
  // 检查错误消息
  const message = errorObj.message;
  if (typeof message === 'string') {
    return message.toLowerCase().includes('timeout');
  }
  
  return false;
}

/**
 * ✅ 检测tRPC错误码（基于错误结构特征，符合最佳实践）
 */
function detectErrorCode(error: unknown): ErrorCode {
  if (!error || typeof error !== 'object') {
    return 'INTERNAL_SERVER_ERROR';
  }
  
  const errorObj = error as TRPCClientErrorLike;

  const responseFirstError = errorObj.meta?.responseJSON?.[0]?.error;

  // 步骤1: trpcCode / 信封 code（优先于 data.code 数值与 shape.data.httpStatus）
  const trpcCodeField =
    (typeof errorObj.data?.trpcCode === 'string' ? errorObj.data.trpcCode : undefined) ||
    (typeof errorObj.shape?.data?.trpcCode === 'string' ? errorObj.shape.data.trpcCode : undefined) ||
    (typeof responseFirstError?.data?.trpcCode === 'string'
      ? responseFirstError.data.trpcCode
      : undefined);

  if (trpcCodeField && isValidTRPCCode(trpcCodeField)) {
    return trpcCodeField;
  }

  const envelopeCode = errorObj.shape?.code;
  if (typeof envelopeCode === 'string' && isValidTRPCCode(envelopeCode)) {
    return envelopeCode;
  }

  const responseEnvelopeCode = responseFirstError?.code;
  if (typeof responseEnvelopeCode === 'string' && isValidTRPCCode(responseEnvelopeCode)) {
    return responseEnvelopeCode;
  }

  // 步骤2: data.code 为 tRPC 字符串码
  const stringCode =
    (typeof errorObj.data?.code === 'string' ? errorObj.data.code : undefined) ||
    (typeof errorObj.shape?.data?.code === 'string' ? errorObj.shape.data.code : undefined) ||
    (typeof responseFirstError?.data?.code === 'string'
      ? responseFirstError.data.code
      : undefined);

  if (stringCode && isValidTRPCCode(stringCode)) {
    return stringCode;
  }

  // 步骤3: data.code 为 HTTP 数值（标准 ErrorResponse.code，如 409）
  const numericCode =
    (typeof errorObj.data?.code === 'number' ? errorObj.data.code : undefined) ||
    (typeof errorObj.shape?.data?.code === 'number' ? errorObj.shape.data.code : undefined) ||
    (typeof responseFirstError?.data?.code === 'number'
      ? responseFirstError.data.code
      : undefined);

  if (numericCode != null && numericCode >= 400 && numericCode < 600) {
    return mapHttpStatusToTRPCCode(numericCode);
  }

  // 步骤4: 从 HTTP 状态码映射（httpStatus 可能来自旧 shape，优先级低于 data.code）
  const httpStatus = errorObj.meta?.response?.status ?? errorObj.data?.httpStatus;

  if (httpStatus) {
    return mapHttpStatusToTRPCCode(httpStatus);
  }
  
  // 步骤3: 网络错误检测（基于结构特征）
  if (checkTimeoutError(error)) {
    return 'TIMEOUT';
  }
  
  if (checkNetworkError(error)) {
      return 'INTERNAL_SERVER_ERROR';
  }
  
  return 'INTERNAL_SERVER_ERROR';
}

/**
 * ✅ 提取错误消息
 */
function extractErrorMessage(error: unknown, code: ErrorCode): string {
  if (!error || typeof error !== 'object') {
    return getFriendlyMessage(code);
  }
  
  // ✅ 网络错误：区分网络连接错误和后端服务器连接错误
  if (checkNetworkError(error)) {
    // 检查网络是否离线（宽带/WiFi中断）
    if (isNetworkOffline()) {
      return '网络连接已断开，请检查您的网络设置（WiFi/宽带）';
    }
    
    // 网络在线但无法连接服务器（可能是服务器未启动或防火墙问题）
    return '无法连接到服务器，请检查服务器状态或联系管理员';
  }
  
  const errorObj = error as TRPCClientErrorLike;

  // ✅ 开发环境：输出错误对象结构（用于调试）
  if (typeof window !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
    try {
      console.log('[extractErrorMessage] Error object structure:', {
        hasMessage: !!errorObj.message,
        message: errorObj.message,
        hasDataMessage: !!errorObj.data?.message,
        dataMessage: errorObj.data?.message,
        hasMetaResponseJSON: !!errorObj.meta?.responseJSON,
        metaResponseJSON: errorObj.meta?.responseJSON,
        hasCause: !!errorObj.cause,
        causeType: errorObj.cause?.constructor?.name,
        causeMessage: (errorObj.cause as { message?: string })?.message,
        errorObjKeys: Object.keys(errorObj),
        // 尝试序列化错误对象（可能失败，但不影响功能）
        fullErrorObj: (() => {
          try {
            return JSON.parse(JSON.stringify(errorObj, (key, value) => {
              // 过滤掉可能导致循环引用的字段
              if (key === 'cause' && value && typeof value === 'object') {
                return { message: (value as { message?: string })?.message, type: value.constructor?.name };
              }
              return value;
            }));
          } catch {
            return '无法序列化错误对象';
          }
        })(),
      });
    } catch (e) {
      console.error('[extractErrorMessage] 调试日志输出失败:', e);
    }
  }

  // ✅ 从所有可能的位置提取消息（按优先级）
  // 注意：当 tRPC 客户端无法转换响应时，后端消息可能还在原始响应中
  // 1. 从 error.data.message（某些情况下，后端消息在这里）
  // 2. 从 error.meta.responseJSON[0].error.message（HTTP 响应 - 最可靠）
  // 3. 从 error.shape?.message（某些 tRPC 版本）
  // 4. 从 error.shape?.data?.message（某些 tRPC 版本）
  // 5. 从 error.cause（如果 cause 是另一个错误对象）
  // 6. 最后才从 error.message（可能是技术性消息）
  
  // ✅ 尝试从 responseJSON 提取（最可靠，因为这是原始 HTTP 响应）
  // 注意：即使 tRPC 客户端转换失败，原始 HTTP 响应数据通常还在 responseJSON 中
  const responseJSON = errorObj.meta?.responseJSON;
  const responseFirstError = Array.isArray(responseJSON) ? responseJSON[0]?.error : undefined;
  const responseJSONDataMessage =
    typeof responseFirstError?.data?.message === 'string'
      ? responseFirstError.data.message
      : undefined;
  const responseJSONMessage =
    responseJSONDataMessage ||
    (typeof responseFirstError?.message === 'string' ? responseFirstError.message : undefined) ||
    (typeof responseJSON === 'object' && responseJSON !== null && !Array.isArray(responseJSON)
      ? (responseJSON as { error?: { message?: string; data?: { message?: string } } }).error?.data
          ?.message ||
        (responseJSON as { error?: { message?: string } }).error?.message
      : undefined);

  // ✅ 尝试从 data.message 提取
  const dataMessage = errorObj.data?.message;

  // ✅ 尝试从 shape 提取
  const shapeMessage = errorObj.shape?.message;
  const shapeDataMessage = errorObj.shape?.data?.message;
  
  // ✅ 尝试从 cause 提取（如果 cause 是错误对象）
  let causeMessage: string | undefined;
  if (errorObj.cause && typeof errorObj.cause === 'object') {
    const causeObj = errorObj.cause as { message?: string; data?: { message?: string } };
    causeMessage = causeObj.message || causeObj.data?.message;
  }

  // ✅ 尝试从原始错误对象的其他属性提取（兜底方案）
  // 某些情况下，消息可能在错误对象的其他属性中
  let otherMessage: string | undefined;
  if (error && typeof error === 'object') {
    const errorAny = error as Record<string, unknown>;
    // 检查常见的位置
    otherMessage = 
      (errorAny.error as { message?: string })?.message ||
      (errorAny.response as { error?: { message?: string } })?.error?.message ||
      (errorAny.body as { error?: { message?: string } })?.error?.message;
  }

  // ✅ 按优先级组合所有可能的消息源
  const backendMessage =
    dataMessage ||
    shapeDataMessage ||
    responseJSONMessage ||
    (shapeMessage && !shapeMessage.includes('Unable to transform') ? shapeMessage : undefined) ||
    causeMessage ||
    otherMessage;

  const topLevelMessage = errorObj.message;

  // ✅ 优先使用后端返回的友好消息
  if (backendMessage && typeof backendMessage === 'string' && backendMessage.length > 0) {
    // ✅ 如果消息包含技术性堆栈跟踪，跳过
    if (backendMessage.includes('at ') && backendMessage.includes('Error:')) {
      // 继续检查其他位置
    } else if (backendMessage.includes('Unable to transform')) {
      // 跳过转换错误消息
    } else {
      // ✅ 直接返回后端提供的友好消息
      return backendMessage;
    }
  }

  // ✅ 如果后端消息不可用，检查顶层消息
  if (topLevelMessage && typeof topLevelMessage === 'string' && topLevelMessage.length > 0) {
    // ✅ 如果消息是 tRPC 的「Unable to transform」，优先用 data.message（后端已在 data 中落 message）
    if (topLevelMessage.includes('Unable to transform response from server')) {
      if (dataMessage && typeof dataMessage === 'string' && !dataMessage.includes('Unable to transform')) {
        return dataMessage;
      }
      const causeMsg = (errorObj.cause as { message?: string; data?: { message?: string } })?.message
        || (errorObj.cause as { message?: string; data?: { message?: string } })?.data?.message;
      if (causeMsg && typeof causeMsg === 'string' && !causeMsg.includes('Unable to transform')) {
        return causeMsg;
      }
      return getFriendlyMessage(code);
    }
    // ✅ 如果消息包含技术性堆栈跟踪，使用友好消息
    if (topLevelMessage.includes('at ') && topLevelMessage.includes('Error:')) {
      return getFriendlyMessage(code);
    }
    // ✅ 直接返回消息（可能是友好的）
    return topLevelMessage;
  }

  // ✅ 如果没有提取到消息，使用默认友好消息
  return getFriendlyMessage(code);
}

/**
 * ✅ 获取友好的错误消息
 */
function getFriendlyMessage(code: ErrorCode): string {
  const messages: Partial<Record<ErrorCode, string>> = {
    'PARSE_ERROR': '数据解析错误',
    'BAD_REQUEST': '请求参数错误',
    'UNAUTHORIZED': '请先登录',
    'FORBIDDEN': '权限不足',
    'NOT_FOUND': '资源不存在',
    'METHOD_NOT_SUPPORTED': '不支持的请求方法',
    'TIMEOUT': '请求超时，请重试',
    'CONFLICT': '资源冲突',
    'PRECONDITION_FAILED': '前置条件失败',
    'PAYLOAD_TOO_LARGE': '请求数据过大',
    'UNPROCESSABLE_CONTENT': '请求参数验证失败',
    'TOO_MANY_REQUESTS': '请求过于频繁，请稍后重试',
    'CLIENT_CLOSED_REQUEST': '请求已取消',
    'INTERNAL_SERVER_ERROR': '服务器错误，请联系管理员',
  };
  
  return messages[code] || '操作失败';
}

/**
 * ✅ 提取Zod验证错误详情
 */
function extractZodError(error: unknown): ErrorInfo['zodError'] | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  
  const errorObj = error as TRPCClientErrorLike;
  const zodError = 
    errorObj.data?.zodError ||
    errorObj.meta?.responseJSON?.[0]?.error?.data?.zodError;
  
  if (!zodError || !zodError.issues || !Array.isArray(zodError.issues)) {
    return undefined;
  }
  
  return {
    issues: zodError.issues.map((issue) => ({
      path: Array.isArray(issue.path) ? issue.path : [],
      message: issue.message,
      code: issue.code || 'custom',
    })),
  };
}

/**
 * ✅ 统一错误处理（核心函数）
 */
export function handleError(
  error: unknown,
  options?: {
    /** 自定义错误消息映射 */
    customMessages?: Partial<Record<ErrorCode, string>>;
  }
): ErrorInfo {
  const code = detectErrorCode(error);
  let message = extractErrorMessage(error, code);
  
  // 应用自定义消息
  if (options?.customMessages?.[code]) {
    message = options.customMessages[code]!;
  }
  
  const errorObj = error && typeof error === 'object' ? (error as TRPCClientErrorLike) : undefined;
  
  return {
    code,
    message,
    httpStatus: errorObj?.data?.httpStatus || errorObj?.meta?.response?.status,
    zodError: extractZodError(error),
    details: errorObj?.data?.details,
    originalError: error,
  };
}

/**
 * ✅ 便捷判断函数：是否为未授权错误
 */
export function isUnauthorizedError(error: unknown): boolean {
  return detectErrorCode(error) === 'UNAUTHORIZED';
}

/**
 * ✅ 便捷判断函数：是否为权限不足错误
 */
export function isForbiddenError(error: unknown): boolean {
  return detectErrorCode(error) === 'FORBIDDEN';
}

/**
 * ✅ 便捷判断函数：是否为验证错误
 */
export function isValidationError(error: unknown): boolean {
  return detectErrorCode(error) === 'UNPROCESSABLE_CONTENT';
}

/**
 * ✅ 便捷判断函数：是否为未找到错误
 */
export function isNotFoundError(error: unknown): boolean {
  return detectErrorCode(error) === 'NOT_FOUND';
}

/**
 * ✅ 便捷判断函数：是否为冲突错误
 */
export function isConflictError(error: unknown): boolean {
  return detectErrorCode(error) === 'CONFLICT';
}

/**
 * ✅ 便捷判断函数：是否为超时错误
 */
export function isTimeoutError(error: unknown): boolean {
  return detectErrorCode(error) === 'TIMEOUT';
}

/**
 * ✅ 便捷判断函数：是否为服务器错误
 */
export function isServerError(error: unknown): boolean {
  return detectErrorCode(error) === 'INTERNAL_SERVER_ERROR';
}

/**
 * ✅ 处理tRPC错误的便捷函数（不带UI，需要调用方处理UI）
 * 
 * 这是一个纯函数版本，不依赖任何UI库
 * 如果需要UI提示，可以在调用方使用 toast 或其他UI库
 * 
 * @param error 错误对象
 * @param options 配置选项
 * @returns 错误信息
 */
export function handleTRPCError(
  error: unknown,
  options?: {
    /** 自定义错误消息映射 */
    customMessages?: Partial<Record<ErrorCode, string>>;
    /** 错误回调（用于显示UI） */
    onError?: (errorInfo: ErrorInfo) => void;
  }
): ErrorInfo {
  const errorInfo = handleError(error, {
    customMessages: options?.customMessages,
  });

  // 调用回调（如果提供）
  options?.onError?.(errorInfo);

  // 开发环境：输出详细信息
  if (typeof window !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
    console.group(`❌ Error [${errorInfo.code}]`);
    console.error('Message:', errorInfo.message);
    console.error('HTTP Status:', errorInfo.httpStatus);
    if (errorInfo.zodError) {
      console.error('Validation Issues:', errorInfo.zodError.issues);
    }
    if (errorInfo.details) {
      console.error('Details:', errorInfo.details);
    }
    console.error('Original:', errorInfo.originalError);
    console.groupEnd();
  }

  return errorInfo;
}

// ========== 向后兼容 ==========

/**
 * @deprecated 使用 ErrorCode (TRPC_ERROR_CODE_KEY) 代替
 */
export enum ErrorType {
  NETWORK_ERROR = 'INTERNAL_SERVER_ERROR',
  TIMEOUT_ERROR = 'TIMEOUT',
  SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  VALIDATION_ERROR = 'UNPROCESSABLE_CONTENT',
  BUSINESS_ERROR = 'BAD_REQUEST',
  UNKNOWN_ERROR = 'INTERNAL_SERVER_ERROR',
}

/**
 * @deprecated 使用 isServerError 代替
 */
export function isNetworkError(error: unknown): boolean {
  return isServerError(error);
}
